/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// UI lib dependencies
import {
	Box,
	Flex,
	Skeleton,
	Image,
	Text,
	Tooltip,
} from '@mantine/core';

// Styles
import classes from './ProductionsSection.module.css';

// Components
import HeaderSection from '../HeaderSection/HeaderSection';
import { VerifiedButton } from '@components/atoms';

// Hooks
import { useTranslation } from 'react-i18next';
import { useAuthorization } from '@lib/authorization';

// Types
import { TProduction } from '@global-types/index';

// Icons
import OverRangeIcon from '@assets/icons/over-range.svg';
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Constants
import { ProductionQuantityCheck } from '@config/constants/enum';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ProductionsSection = (props: {
	isLoading: boolean;
	data: TProduction[] | [];
	onSeeAll: () => void;
}) => {
	const {
		isLoading,
		onSeeAll,
		data: productions,
	} = props;
	/* ********************************** HOOKS ********************************* */

	const { t } = useTranslation();
	const { checkRole } = useAuthorization();

	if (isLoading)
		return (
			<>
				{[...Array(5)].map((_, rowIndex) => (
					<Flex
						key={rowIndex}
						className={classes.containerSkeletons}
						gap="md"
						style={{ width: '100%', marginBottom: 8 }}
					>
						{[...Array(8)].map((_, colIndex) => (
							<Skeleton
								key={colIndex}
								height={40}
								width="80%"
								radius="md"
							/>
						))}
					</Flex>
				))}
			</>
		);

	return (
		<Box className={classes.container}>
			<HeaderSection
				title={t('global.lastProductions')}
				onSeeAll={onSeeAll}
			/>
			{productions && productions.length > 0 && (
				<table className={classes.tableStyle}>
					<thead>
						<tr>
							<th className={classes.thStyle}>
								{t('global.project')}
							</th>
							<th className={classes.thStyle}>
								{t('global.startDate')}
							</th>
							<th className={classes.thStyle}>
								{t('global.production')}
							</th>
							<th className={classes.thStyle}>
								{t('global.status')}
							</th>
							<th className={classes.thStyle}>
								{t('global.qtyAlert')}
							</th>
							{checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<th
									className={classes.thStyleCertificate}
								>
									{t('global.certificate')}
								</th>
							)}
						</tr>
					</thead>
					<tbody>
						{productions.map(
							(row: TProduction, index) => (
								<tr key={index}>
									<td className={classes.tdStyle}>
										{row.project}
									</td>
									<td className={classes.tdStyle}>
										{row.startDate}
									</td>
									<td className={classes.tdStyle}>
										{row.production}
									</td>
									<td className={classes.tdStyle}>
										<VerifiedButton
											readOnly={true}
											isverified={
												row && row.verified
													? row.verified.toString()
													: undefined
											}
										>
											{row && row.verified
												? t('global.verified')
												: t('global.notVerified')}
										</VerifiedButton>
									</td>
									<td className={classes.tdStyle}>
										{row.quantityCheck ==
											ProductionQuantityCheck.OVER_RANGE && (
											<Tooltip
												label={t('global.overRange')}
											>
												<Image
													className={classes.tdOverrange}
													h={'20px'}
													w={'20px'}
													src={OverRangeIcon}
												/>
											</Tooltip>
										)}
										{row.quantityCheck ==
											ProductionQuantityCheck.UNDER_RANGE && (
											<Tooltip
												label={t('global.underRange')}
											>
												<Image
													style={{
														transform: 'rotate(180deg)',
													}}
													className={classes.tdOverrange}
													h={'20px'}
													w={'20px'}
													src={OverRangeIcon}
												/>
											</Tooltip>
										)}
									</td>
								</tr>
							)
						)}
					</tbody>
				</table>
			)}
			{productions.length == 0 && (
				<Box className={classes.containerTextsNoRecords}>
					<Image
						src={NoRecordsIcon}
						className={classes.iconNoRecords}
					/>
					<Text className={classes.titleNoRecords}>
						{t('global.datatable.noResultsFound')}
					</Text>
				</Box>
			)}
		</Box>
	);
};
